import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { selectUser, selectProfileLoaded } from '../../features/auth/selectors/auth.selectors';
import { TokenManager } from '../../utils/storage/tokenmanager.storage';
import LoadingSpinner from '../ui/LoadingSpinner';

interface RequireProfileCompleteProps {
  children: JSX.Element;
}

const RequireProfileComplete: React.FC<RequireProfileCompleteProps> = React.memo(({ children }) => {
  const user = useAppSelector(selectUser);
  const profileLoaded = useAppSelector(selectProfileLoaded);
  const location = useLocation();

  if (!user) {
    // Should be wrapped in RequireAuth, but safe fallback
    const loginPath = location.pathname.startsWith('/admin') ? '/admin/login' : '/login';
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // Check if profile is complete (firstName and lastName are present)
  const isProfileComplete = Boolean(user.firstName && user.lastName);

  // If profile looks incomplete but we haven't fetched fresh data yet,
  // wait for getProfile to finish before redirecting (cached data may be stale)
  if (!isProfileComplete && !profileLoaded && TokenManager.isAuthenticated()) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" color="orange" />
      </div>
    );
  }

  if (!isProfileComplete) {
    const profileSetupPath = location.pathname.startsWith('/admin')
      ? '/admin/profile-setup'
      : '/profile-setup';

    if (location.pathname === profileSetupPath) {
      return children;
    }

    return <Navigate to={profileSetupPath} replace />;
  }

  return children;
});

export default RequireProfileComplete;
